import { Injectable } from '@angular/core';
import { forkJoin, Observable } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { ConceptsModal, PageRequest } from '../../domain/concepts-modal.model';

import { API_CONFIG, BaseCrudService, PageResponse } from '@nebula-shared/data-access';

@Injectable({ providedIn: 'root' })
export class ConceptsService extends BaseCrudService<ConceptsModal> {
  constructor() {
    super();
    const config = API_CONFIG.accounting;
    this.setBaseUrl(`${config.baseUrl}${config.endpoints.conceptosContables}`);
  }

  /**
   * Método privado para hacer la petición POST al endpoint page-response
   * solo estado ACTIVE
   */
  private postPageResponse(page = 0, size = 10): Observable<any> {
    const body: PageRequest = {
      page: page.toString(),
      size: size.toString(),
      searchsBy: [],
      ordersBy: [],
    };

    return this.http.post<any>(`${this.baseUrl}/page-response`, body);
  }

  private extractDataFromResponse(response: any): any {
    const data = response?.response?.content || response?.content || response;
    return {
      content: data?.content || [],
      totalElements: data?.totalElements || 0,
      totalPages: data?.totalPages || 0,
      size: data?.size || 10,
      number: data?.number || 0,
    };
  }

  getAllConcepts(): Observable<ConceptsModal[]> {
    return this.postPageResponse(0, 10).pipe(
      switchMap((response) => {
        const data = this.extractDataFromResponse(response);
        const totalPages = data.totalPages;
        const pageSize = data.size;

        if (totalPages <= 1) {
          return this.mapContent(data.content);
        }

        const requests$: Observable<any>[] = [];
        for (let page = 1; page < totalPages; page++) {
          requests$.push(this.postPageResponse(page, pageSize));
        }

        return forkJoin(requests$).pipe(
          switchMap((responses) => {
            let allContent = [...data.content];
            responses.forEach((res) => {
              const pageData = this.extractDataFromResponse(res);
              allContent = [...allContent, ...pageData.content];
            });
            return this.mapContent(allContent);
          }),
        );
      }),
    );
  }

  private mapContent(content: any[]): Observable<ConceptsModal[]> {
    const mapped = content.map((item: any) => ({
      ...item,
      estadoConcepto: item.estadoConcepto || 'ACTIVO',
    }));
    return new Observable((observer) => {
      observer.next(mapped);
      observer.complete();
    });
  }

  getConceptsPaginados(
    page = 0,
    size = 10,
    searchTerm = '',
    _soloActivos = true,
  ): Observable<PageResponse<ConceptsModal>> {
    return this.getAllConcepts().pipe(
      map((allConcepts) => {
        let filtered = allConcepts;

        if (searchTerm && searchTerm.trim() !== '') {
          const term = searchTerm.toLowerCase().trim();
          filtered = filtered.filter(
            (concept) =>
              concept.codigo?.toLowerCase().includes(term) ||
              concept.nombre.toLowerCase().includes(term),
          );
        }

        filtered.sort((a, b) => a.codigo.localeCompare(b.codigo));

        const start = page * size;
        const end = start + size;
        const paginatedContent = filtered.slice(start, end);

        return {
          content: paginatedContent,
          totalElements: filtered.length,
          totalPages: Math.ceil(filtered.length / size),
          size: size,
          number: page,
          numberOfElements: paginatedContent.length,
          first: page === 0,
          last: end >= filtered.length,
          empty: paginatedContent.length === 0,
        } as PageResponse<ConceptsModal>;
      }),
    );
  }
}
